import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { startTimetableRun, getTimetableRun, getLatestTimetableRun, listTimetableRuns } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const STATUS_LABEL = {
  running: "Đang xếp lịch...",
  done: "Hoàn tất",
  failed: "Thất bại",
};
const DAY_LABELS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "");

// Chạy xếp TKB cho 1 tuần đã chọn trong tree (weekId truyền vào).
const HappySchoolRunTKB = ({ weekId, onRan }) => {
  const schoolId = getActiveSchoolId();
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  // Danh sách bản đã xếp của tuần này — dùng làm nguồn "khoá cứng theo ngày".
  const [pastRuns, setPastRuns] = useState([]);
  const [freezeOn, setFreezeOn] = useState(false);
  const [freezeRunId, setFreezeRunId] = useState("");
  const [freezeDays, setFreezeDays] = useState([]); // ngày giữ nguyên (khoá cứng)

  useEffect(() => {
    if (!schoolId || !weekId) return;
    listTimetableRuns(schoolId, weekId)
      .then((list) => {
        const done = (list || []).filter((r) => r.status === "done");
        setPastRuns(done);
        const published = done.find((r) => r.published);
        setFreezeRunId(published ? published.id : (done[0] ? done[0].id : ""));
      })
      .catch(() => {});
  }, [schoolId, weekId]);

  // Lấy lần xếp gần nhất; chỉ hiện nếu đúng tuần đang chọn.
  useEffect(() => {
    if (!schoolId || !weekId) return;
    setRun(null);
    setError("");
    getLatestTimetableRun(schoolId)
      .then((r) => {
        if (r && r.weekId === weekId) setRun(r);
      })
      .catch(() => {});
  }, [schoolId, weekId]);

  useEffect(() => {
    if (run && run.status === "running") {
      pollRef.current = setInterval(async () => {
        try {
          const updated = await getTimetableRun(schoolId, run.id);
          setRun(updated);
          if (updated.status !== "running") {
            clearInterval(pollRef.current);
            if (onRan) onRan();
          }
        } catch (err) {
          setError(err.message);
          clearInterval(pollRef.current);
        }
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [run, schoolId]);

  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;

  const toggleFreezeDay = (d) => setFreezeDays((arr) => (arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort()));

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      const freeze = freezeOn && freezeRunId && freezeDays.length ? { runId: freezeRunId, days: freezeDays } : null;
      const { runId } = await startTimetableRun(schoolId, weekId, freeze);
      setRun({ id: runId, status: "running", weekId });
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const running = starting || run?.status === "running";
  const freezeReady = !freezeOn || (freezeRunId && freezeDays.length > 0);

  return (
    <div>
      <p style={{ color: "#4b5563", fontSize: 13, marginTop: 0 }}>
        Chạy để xếp thời khoá biểu tự động cho tuần này, dựa trên phân công chuyên môn, lịch bận của
        giáo viên, và lịch phân phối nghề-văn hoá đã khai báo.
      </p>

      {pastRuns.length > 0 && (
        <div style={freezeBoxStyle}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={freezeOn} onChange={(e) => setFreezeOn(e.target.checked)} />
            Chỉ xếp lại 1 số ngày, giữ nguyên (khoá cứng) các ngày còn lại theo 1 bản đã có
          </label>
          {freezeOn && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <span style={{ fontSize: 13, color: "#4b5563", marginRight: 8 }}>Lấy nguyên trạng từ:</span>
                <select value={freezeRunId} onChange={(e) => setFreezeRunId(e.target.value)} style={{ padding: 6, fontSize: 13 }}>
                  {pastRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {fmtTime(r.finishedAt || r.createdAt)}{r.published ? " · Đã công bố" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <span style={{ fontSize: 13, color: "#4b5563", marginRight: 8, display: "block", marginBottom: 4 }}>
                  Giữ nguyên (khoá cứng) các ngày:
                </span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {DAY_LABELS.map((label, d) => (
                    <label key={d} style={dayChipStyle(freezeDays.includes(d))}>
                      <input type="checkbox" checked={freezeDays.includes(d)} onChange={() => toggleFreezeDay(d)} style={{ marginRight: 4 }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px" }}>
                ⚠ Các ngày đã chọn sẽ bị ép cứng giữ nguyên (không đổi), CP-SAT chỉ được tự do xếp các ngày còn lại.
                Các ô đã khoá (kể cả Case xếp tay) luôn được giữ nguyên, không bao giờ bị xếp đè —
                nếu không đủ chỗ để xếp đủ tiết/tuần cho các ngày còn lại thì hệ thống chấp nhận thiếu tiết,
                báo rõ ở Báo cáo, chứ không phá khoá.
              </p>
            </div>
          )}
        </div>
      )}

      <button onClick={handleStart} disabled={running || !freezeReady} style={{ ...btnStyle, opacity: running || !freezeReady ? 0.6 : 1 }}>
        {running ? "⏳ Đang xếp lịch..." : "🚀 Xếp thời khoá biểu"}
      </button>

      {error && <p style={{ color: "#dc2626", marginTop: 12 }}>{error}</p>}

      {run && (
        <div style={boxStyle}>
          <p style={{ margin: "0 0 8px" }}>
            Trạng thái: <strong>{STATUS_LABEL[run.status] || run.status}</strong>
            {run.params && (
              <span style={{ color: "#6b7280", fontSize: 13 }}> — {run.params.numClasses} lớp, {run.params.numTeachers} giáo viên</span>
            )}
          </p>
          {run.freezeDays && run.freezeDays.length > 0 && (
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "#0f766e" }}>
              🔒 Đã giữ nguyên: {run.freezeDays.map((d) => DAY_LABELS[d]).join(", ")}
              {run.hardLocksDowngraded && <span style={{ color: "#b45309" }}> (đã phải mềm hoá bớt vì quá kẹt)</span>}
            </p>
          )}
          {run.status === "failed" && run.error && <p style={{ color: "#dc2626", margin: 0 }}>{run.error}</p>}
          {run.status === "done" && (
            <Link to={`/happyschool/timetable/${run.id}`} style={{ ...btnStyle, display: "inline-block", textDecoration: "none" }}>
              📋 Xem kết quả
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

const btnStyle = { padding: "10px 20px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const boxStyle = { marginTop: 20, padding: 16, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 };
const freezeBoxStyle = { marginBottom: 16, padding: 14, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8 };
const dayChipStyle = (active) => ({
  display: "flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 13, cursor: "pointer",
  border: active ? "1px solid #0ea5a3" : "1px solid #d1d5db", background: active ? "#f0fdfa" : "#fff", color: active ? "#0f766e" : "#374151",
});

export default HappySchoolRunTKB;
